/**
 * Audit log tab (admin only) — read-only viewer of recent AI runs.
 *
 * Reads GET /api/admin/audit (CLAUDE.md §8). A filterable table of the tenant's
 * ai_audit_log rows (feature_key, run_type, model, tokens, cost, status, created
 * + 👍/👎 feedback counts), newest first. A row click expands to show the system
 * + user prompt and the response (monospace, scrollable). No API keys live in
 * this table, so showing prompts/responses is safe for admins.
 *
 * Filters (feature_key, run_type, date range, limit) are committed via an Apply
 * button into the applied state that drives the query, mirroring the dashboard's
 * draft→applied pattern. This is a viewer, not a save form — there's no mutation
 * and nothing to invalidate.
 */
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type { AuditEntry, AuditFilters } from "@/types";
import { Button, Card, SelectField, Badge } from "@/components/ui";
import { LoadingState } from "@/components/LoadingState";
import { ErrorState } from "@/components/ErrorState";
import { IconChevronDown } from "@/components/icons";

const LIMIT = 100;

interface FilterDraft {
  feature_key: string;
  run_type: string;
  from: string; // yyyy-mm-dd (local date input)
  to: string;
}

const EMPTY: FilterDraft = { feature_key: "", run_type: "", from: "", to: "" };

/** Build the server query from the committed filters (only set params are sent). */
function toQuery(f: FilterDraft): AuditFilters {
  return {
    ...(f.feature_key ? { feature_key: f.feature_key } : {}),
    ...(f.run_type ? { run_type: f.run_type } : {}),
    ...(f.from ? { from: new Date(`${f.from}T00:00:00`).toISOString() } : {}),
    ...(f.to ? { to: new Date(`${f.to}T23:59:59.999`).toISOString() } : {}),
    limit: LIMIT,
  };
}

function fmtDateTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString();
}

/** Integer cents → "$0.0123" (4 dp so sub-cent AI costs stay legible). null → —. */
function fmtCost(cents: number | null): string {
  if (cents === null) return "—";
  return `$${(cents / 100).toFixed(4)}`;
}

function fmtNum(n: number | null): string {
  return n === null ? "—" : n.toLocaleString();
}

function statusTone(status: string): "success" | "warning" | "danger" | "muted" {
  if (status === "ok" || status === "success") return "success";
  if (status === "error" || status === "failed") return "danger";
  if (status === "timeout") return "warning";
  return "muted";
}

export function AuditLogTab() {
  const [draft, setDraft] = useState<FilterDraft>(EMPTY);
  const [applied, setApplied] = useState<FilterDraft>(EMPTY);

  const query = useMemo(() => toQuery(applied), [applied]);

  const auditQuery = useQuery({
    queryKey: ["admin", "audit", query],
    queryFn: ({ signal }) => api.adminGetAudit(query, signal),
  });

  const rows = auditQuery.data?.rows ?? [];

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h2 className="text-sm font-semibold">AI audit log</h2>
        <p className="text-sm text-muted-foreground">
          Every production AI run (and test variants) with model, tokens, cost,
          status, and the feedback it received. Click a row to inspect the exact
          prompt and response.
        </p>
      </div>

      {/* Filters */}
      <Card className="flex flex-col gap-4">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <SelectField
            label="Feature"
            value={draft.feature_key}
            onChange={(e) => setDraft({ ...draft, feature_key: e.target.value })}
            options={[
              { value: "", label: "All features" },
              { value: "call_scoring", label: "Call scoring" },
              { value: "call_classify", label: "Call classify" },
              { value: "live_coaching", label: "Live coaching" },
            ]}
          />
          <SelectField
            label="Run type"
            value={draft.run_type}
            onChange={(e) => setDraft({ ...draft, run_type: e.target.value })}
            options={[
              { value: "", label: "All runs" },
              { value: "production", label: "Production" },
              { value: "test", label: "Test variant" },
            ]}
          />
          <label className="flex flex-col gap-1 text-sm">
            <span className="font-medium">From</span>
            <input
              type="date"
              value={draft.from}
              max={draft.to || undefined}
              onChange={(e) => setDraft({ ...draft, from: e.target.value })}
              className="rounded-md border bg-background px-3 py-1.5 text-sm outline-none focus:ring-2 focus:ring-ring"
            />
          </label>
          <label className="flex flex-col gap-1 text-sm">
            <span className="font-medium">To</span>
            <input
              type="date"
              value={draft.to}
              min={draft.from || undefined}
              onChange={(e) => setDraft({ ...draft, to: e.target.value })}
              className="rounded-md border bg-background px-3 py-1.5 text-sm outline-none focus:ring-2 focus:ring-ring"
            />
          </label>
        </div>
        <div className="flex items-center justify-end gap-2">
          <Button
            variant="ghost"
            onClick={() => {
              setDraft(EMPTY);
              setApplied(EMPTY);
            }}
          >
            Clear
          </Button>
          <Button variant="primary" onClick={() => setApplied(draft)}>
            Apply
          </Button>
        </div>
      </Card>

      {/* Table */}
      {auditQuery.isLoading && <LoadingState label="Loading audit log…" />}
      {auditQuery.isError && (
        <ErrorState error={auditQuery.error} onRetry={() => void auditQuery.refetch()} />
      )}
      {auditQuery.isSuccess && rows.length === 0 && (
        <p className="text-sm text-muted-foreground">
          No AI runs match these filters.
        </p>
      )}
      {rows.length > 0 && (
        <Card className="overflow-hidden p-0">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[920px] text-sm">
              <thead className="border-b bg-muted/40 text-left text-xs text-muted-foreground">
                <tr>
                  <th className="px-4 py-2 font-medium">When</th>
                  <th className="px-4 py-2 font-medium">Feature</th>
                  <th className="px-4 py-2 font-medium">Run</th>
                  <th className="px-4 py-2 font-medium">Model</th>
                  <th className="px-4 py-2 text-right font-medium">Tokens (in/out)</th>
                  <th className="px-4 py-2 text-right font-medium">Cost</th>
                  <th className="px-4 py-2 text-right font-medium">Dur.</th>
                  <th className="px-4 py-2 font-medium">Status</th>
                  <th className="px-4 py-2 font-medium">Feedback</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <AuditRow key={row.id} row={row} />
                ))}
              </tbody>
            </table>
          </div>
          <p className="border-t px-4 py-2 text-xs text-muted-foreground">
            Showing the {rows.length} most recent run{rows.length === 1 ? "" : "s"}
            {rows.length >= LIMIT ? ` (capped at ${LIMIT})` : ""}.
          </p>
        </Card>
      )}
    </div>
  );
}

/** One audit row: a scan line that expands to show prompt + response. */
function AuditRow({ row }: { row: AuditEntry }) {
  const [open, setOpen] = useState(false);

  const variant =
    row.run_type === "test" && row.variant_label
      ? `test · ${row.variant_label}`
      : row.run_type;

  return (
    <>
      <tr
        onClick={() => setOpen((v) => !v)}
        className="cursor-pointer border-b last:border-0 hover:bg-accent/40"
        aria-expanded={open}
      >
        <td className="px-4 py-2 text-muted-foreground tabular-nums">
          {fmtDateTime(row.created_at)}
        </td>
        <td className="px-4 py-2">{row.feature_key}</td>
        <td className="px-4 py-2 text-muted-foreground">{variant}</td>
        <td className="px-4 py-2 text-muted-foreground">{row.model ?? "—"}</td>
        <td className="px-4 py-2 text-right tabular-nums text-muted-foreground">
          {fmtNum(row.tokens_in)} / {fmtNum(row.tokens_out)}
        </td>
        <td className="px-4 py-2 text-right tabular-nums text-muted-foreground">
          {fmtCost(row.cost_cents)}
        </td>
        <td className="px-4 py-2 text-right tabular-nums text-muted-foreground">
          {row.duration_ms === null ? "—" : `${row.duration_ms} ms`}
        </td>
        <td className="px-4 py-2">
          <Badge tone={statusTone(row.status)}>{row.status}</Badge>
        </td>
        <td className="px-4 py-2">
          <span className="inline-flex items-center gap-2 text-xs">
            <span className="text-band-strong">👍 {row.up_count}</span>
            <span className="text-band-weak">👎 {row.down_count}</span>
            <IconChevronDown
              className={`h-4 w-4 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`}
            />
          </span>
        </td>
      </tr>
      {open && (
        <tr className="border-b last:border-0 bg-muted/20">
          <td colSpan={9} className="px-4 py-3">
            <div className="flex flex-col gap-3">
              <PromptBlock label="System prompt" value={row.system_prompt} />
              <PromptBlock label="User prompt" value={row.user_prompt} />
              <PromptBlock label="Response" value={row.response} />
              {row.provider && (
                <p className="text-xs text-muted-foreground">
                  Provider: {row.provider}
                  {row.rubric_version !== null
                    ? ` · rubric v${row.rubric_version}`
                    : ""}
                </p>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

/** A labeled, scrollable monospace block for a prompt/response. */
function PromptBlock({ label, value }: { label: string; value: string | null }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {label}
      </span>
      {value ? (
        <pre className="max-h-64 overflow-auto whitespace-pre-wrap rounded-md border bg-background px-3 py-2 font-mono text-xs leading-relaxed">
          {value}
        </pre>
      ) : (
        <p className="text-xs text-muted-foreground">—</p>
      )}
    </div>
  );
}
